import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'
import cx from 'classnames'
import { useForm, Controller } from 'react-hook-form'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { StylesProvider } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import DashboardTooltip from 'modules/Dashboard/components/shared/DashboardTooltip/DashboardTooltip'
import styles from './DownloadSettingsForm.module.scss'
import moment from 'moment'
import { ArrowBack, ArrowForward } from '@material-ui/icons'
import { LoadingButton } from '@mui/lab'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { dataGroupingDefault, dataGroupingValues } from 'constants/DataGrouping'
import { AppDatePicker, toDateInputValue } from 'common/components/DatePicker'

const DOWNLOAD_STEP = {
  PARAMETERS: 'parameters',
  DATE_AND_FORMAT: 'dateAndFormat'
}

const selectMenuProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null,
  MenuListProps: {
    className: styles.selectMenuList
  },
  PaperProps: {
    className: styles.selectMenuPaper
  }
}

const DownloadSettingsSelect = ({ children, id, onChange, renderValue, value }) => (
  <FormControl>
    <Select
      id={id}
      value={value}
      displayEmpty
      renderValue={renderValue}
      onChange={onChange}
      variant='standard'
      disableUnderline
      className={styles.select}
      IconComponent={KeyboardArrowDownIcon}
      classes={{
        icon: styles.selectIcon,
        select: styles.selectFocus
      }}
      MenuProps={selectMenuProps}
    >
      {children}
    </Select>
  </FormControl>
)

const DownloadSettingsMenuItem = ({ children, value }) => (
  <MenuItem
    value={value}
    className={styles.menuItem}
    classes={{
      selected: styles.menuItemSelected
    }}
  >
    {children}
  </MenuItem>
)

const DownloadStepNav = ({ activeStep, translate }) => (
  <div className={styles.nav}>
    <Typography
      className={
        activeStep === DOWNLOAD_STEP.PARAMETERS
          ? styles.subheaderParamsSelected
          : styles.subheaderParams
      }
    >
      {translate('editNavParams')}
    </Typography>
    <Typography
      className={
        activeStep === DOWNLOAD_STEP.DATE_AND_FORMAT
          ? styles.subheaderDateandFormatSelected
          : styles.subheaderDateandFormat
      }
    >
      {translate('navDateAndFormat')}
    </Typography>
  </div>
)

const DownloadParametersStep = ({
  errors,
  onCancel,
  onNext,
  onSelectAll,
  onTypeChange,
  register,
  selectAllCheckboxRef,
  translate,
  typeChecked,
  types
}) => (
  <div className={styles.firstPage}>
    <DownloadStepNav
      activeStep={DOWNLOAD_STEP.PARAMETERS}
      translate={translate}
    />
    <div className={styles.subInfoWrapper}>
      <Typography className={styles.subInfo}>
        {translate('downloadDataForm.selectParameters')}
      </Typography>
    </div>
    <FormControlLabel
      className={styles.selectAllCheckboxForm}
      control={
        <input
          checked={types.length > 0 && typeChecked.length === types.length}
          className={styles.checkbox}
          name='selectAll'
          id='selectAll'
          onChange={onSelectAll}
          ref={selectAllCheckboxRef}
          type='checkbox'
        />
      }
      label={translate('downloadDataForm.selectAll')}
    />
    <div className={styles.checkboxWrapper}>
      {types.map((dataType, i) => (
        <div
          className={styles.checkboxFormDiv}
          key={dataType}
        >
          <FormControlLabel
            className={styles.checkboxForm}
            control={
              <input
                checked={typeChecked.includes(dataType)}
                className={styles.checkbox}
                onChange={onTypeChange}
                required
                name={`types[${i}]`}
                value={dataType}
                ref={register()}
                type='checkbox'
              />
            }
            label={translate(dataType)}
          />
        </div>
      ))}
    </div>
    {errors.types && (
      <div className={styles.error}>
        <small>{translate('validation.anyFieldRequired')}</small>
      </div>
    )}
    <div className={styles.buttonWrapper1}>
      <Button
        fullWidth
        variant='contained'
        className={styles.buttonSiguiente}
        onClick={onNext}
      >
        {translate('next')}
        <ArrowForward className={styles.arrow} />
      </Button>
    </div>
    <div className={styles.buttonWrapper}>
      <Button
        fullWidth
        variant='contained'
        className={styles.buttonCancelar}
        onClick={onCancel}
      >
        {translate('cancel')}
      </Button>
    </div>
  </div>
)

const DownloadDateAndFormatStep = ({
  control,
  errors,
  fileExtensionValue,
  getDownloadMinDate,
  getFileExtensionLabel,
  getGroupingLabel,
  getOrderLabel,
  groupingValue,
  isPremium,
  isSubmitting,
  onBack,
  onCancel,
  onChangeFileExtension,
  onChangeGrouping,
  onChangeOrder,
  orderValue,
  submitError,
  system,
  translate,
  validateDates
}) => (
  <div className={styles.secondPage}>
    <DownloadStepNav
      activeStep={DOWNLOAD_STEP.DATE_AND_FORMAT}
      translate={translate}
    />
    <div className={styles.dataGroupingWrapper}>
      <Typography className={styles.selectLabel}>
        {translate('downloadDataForm.dataGrouping')}
      </Typography>
      <DownloadSettingsSelect
        id='dataGrouping'
        value={groupingValue}
        renderValue={getGroupingLabel}
        onChange={onChangeGrouping}
      >
        <DownloadSettingsMenuItem value={dataGroupingDefault.value}>
          {translate(dataGroupingDefault.translation)}
        </DownloadSettingsMenuItem>
        {dataGroupingValues
          .filter(item => system.timeInterval <= item.minutes)
          .map(item => (
            <DownloadSettingsMenuItem
              key={item.value}
              value={item.value}
            >
              {translate(item.translation)}
            </DownloadSettingsMenuItem>
          ))}
      </DownloadSettingsSelect>
    </div>
    <div>
      <Typography className={styles.subInfo}>
        {translate('downloadDataForm.selectDateInterval')}
      </Typography>
    </div>
    <DashboardTooltip
      arrow
      label={!isPremium ? translate('businessPlan') : ''}
      placement='right'
    >
      <div className={styles.getMoreRange}>
        {translate('downloadDataForm.availableRange')}
        <span className={styles.infoIcon}>i</span>
      </div>
    </DashboardTooltip>
    <div className={styles.datePickerWrapper}>
      <div className={styles.picker}>
        <Controller
          control={control}
          name='startDate'
          defaultValue=''
          rules={{ required: true }}
          render={({ onChange, value }) => (
            <AppDatePicker
              disableFuture
              error={Boolean(errors.startDate)}
              inputClassName={styles.datePicker}
              label={translate('startDate')}
              min={getDownloadMinDate()}
              name='startDatePicker'
              onChange={onChange}
              required
              value={value}
            />
          )}
        />
      </div>
      {errors.startDate && (
        <div className={styles.error}>
          <small>{translate('validation.required')}</small>
        </div>
      )}
      <div className={styles.picker}>
        <Controller
          control={control}
          name='endDate'
          defaultValue=''
          rules={{ required: true, validate: validateDates }}
          render={({ onChange, value }) => (
            <AppDatePicker
              disableFuture
              error={Boolean(errors.endDate)}
              inputClassName={styles.datePicker}
              label={translate('endDate')}
              min={getDownloadMinDate()}
              name='endDatePicker'
              onChange={onChange}
              required
              value={value}
            />
          )}
        />
      </div>
      {errors.endDate && errors.endDate?.type === 'required' && (
        <div className={styles.error}>
          <small>{translate('validation.required')}</small>
        </div>
      )}
      {errors.endDate && errors.endDate?.type === 'validate' && (
        <div className={styles.error}>
          <small>{translate('validation.invalidDateRange')}</small>
        </div>
      )}
    </div>
    <div className={styles.fileExtensionWrapper}>
      <Typography className={styles.selectLabel}>
        {translate('downloadDataForm.downloadOrder')}
      </Typography>
      <DownloadSettingsSelect
        id='orderSelect'
        value={orderValue}
        renderValue={getOrderLabel}
        onChange={onChangeOrder}
      >
        <DownloadSettingsMenuItem value='0'>
          {translate('downloadDataForm.olderDataFirst')}
        </DownloadSettingsMenuItem>
        <DownloadSettingsMenuItem value='1'>
          {translate('downloadDataForm.latestDataFirst')}
        </DownloadSettingsMenuItem>
      </DownloadSettingsSelect>
    </div>
    <div className={styles.fileExtensionWrapper}>
      <Typography className={styles.selectLabel}>
        {translate('downloadDataForm.downloadExtension')}
      </Typography>
      <DownloadSettingsSelect
        id='extensionFileSelect'
        value={fileExtensionValue}
        renderValue={getFileExtensionLabel}
        onChange={onChangeFileExtension}
      >
        <DownloadSettingsMenuItem value='csv'>{translate('csv')}</DownloadSettingsMenuItem>
        <DownloadSettingsMenuItem value='xlsx'>{translate('xlsx')}</DownloadSettingsMenuItem>
      </DownloadSettingsSelect>
    </div>
    <div className={cx(styles.buttonWrapper, styles.downloadButtonWrapper)}>
      {submitError && <div className={styles.noDataMessage}>{submitError}</div>}
      <LoadingButton
        type='submit'
        fullWidth
        loading={isSubmitting}
        loadingPosition='end'
        variant='contained'
        className={styles.buttonSiguiente}
      >
        {translate('download')}
      </LoadingButton>
    </div>
    <div className={styles.buttonWrapper}>
      <Button
        fullWidth
        disabled={isSubmitting}
        variant='contained'
        className={styles.buttonCancelar}
        onClick={onBack}
      >
        <ArrowBack className={styles.arrow} />
        {translate('back')}
      </Button>
    </div>
    <div className={styles.buttonWrapper}>
      <Button
        fullWidth
        disabled={isSubmitting}
        variant='contained'
        className={styles.buttonCancelar}
        onClick={onCancel}
      >
        {translate('cancel')}
      </Button>
    </div>
  </div>
)

const DownloadSettingsForm = props => {
  const { onSubmit, translate, system, sortedData, isPremium, submitError } = props
  const { handleSubmit, register, errors, control, setError, clearErrors, formState, getValues } =
    useForm()
  const { isSubmitting } = formState

  const [value, setValue] = useState('csv')
  const [orderValue, setOrderValue] = useState('0')
  const [groupingValue, setGroupingValue] = useState('c')
  const [activeStep, setActiveStep] = useState(DOWNLOAD_STEP.PARAMETERS)
  const [typeChecked, setTypeChecked] = useState([])
  const selectAllCheckboxRef = useRef(null)

  const types = useMemo(() => {
    const dataTypes = sortedData.map(item => item.type)

    if (system.weather && system.weather.baseStationLocation) {
      dataTypes.push('weather')
    }

    return dataTypes
  }, [sortedData, system])

  const selectedTypes = types.filter(dataType => typeChecked.includes(dataType))

  useEffect(() => {
    setTypeChecked(previousTypeChecked => {
      const nextTypeChecked = previousTypeChecked.filter(dataType => types.includes(dataType))

      return nextTypeChecked.length === previousTypeChecked.length
        ? previousTypeChecked
        : nextTypeChecked
    })
  }, [types])

  const handleChangeOrder = event => {
    setOrderValue(event.target.value)
  }

  const handleChangeFileExtension = event => {
    setValue(event.target.value)
  }

  const handleChangeGrouping = event => {
    setGroupingValue(event.target.value)
  }

  const getGroupingLabel = selectedValue => {
    const groupingOptions = [dataGroupingDefault, ...dataGroupingValues]
    const groupingOption = groupingOptions.find(item => item.value === selectedValue)

    return groupingOption ? translate(groupingOption.translation) : selectedValue
  }

  const getOrderLabel = selectedValue => {
    if (selectedValue === '0') return translate('downloadDataForm.olderDataFirst')
    if (selectedValue === '1') return translate('downloadDataForm.latestDataFirst')
    return selectedValue
  }

  const getFileExtensionLabel = selectedValue => translate(selectedValue)

  const setTypeSelectionError = nextTypeChecked => {
    if (nextTypeChecked.length === 0) {
      setError('types', { shouldFocus: 'types' })
    } else {
      clearErrors('types')
    }
  }

  const handleChange = event => {
    if (event.target.type === 'radio') {
      setValue(event.target.value)
      return
    }

    if (event.target.type !== 'checkbox') return

    const { checked, value } = event.target
    const nextTypeChecked = checked
      ? [...new Set([...typeChecked, value])]
      : typeChecked.filter(item => item !== value)

    setTypeChecked(nextTypeChecked)
    setTypeSelectionError(nextTypeChecked)
  }

  const onClickCancel = () => {
    props.hideModal()
  }

  const handleSelectAll = () => {
    const nextTypeChecked = typeChecked.length === types.length ? [] : types

    setTypeChecked(nextTypeChecked)
    setTypeSelectionError(nextTypeChecked)
  }

  const checkInputs = () => {
    if (moment(getValues('endDate')) < moment(getValues('startDate'))) return 'Invalid date'

    const data = {}
    data.weather = selectedTypes.includes('weather')
    data.params = selectedTypes.filter(dataType => dataType !== 'weather')
    data.endDate = getValues('endDate')
    data.startDate = getValues('startDate')
    data.fileType = value
    data.order = orderValue
    data.interval = groupingValue

    return data
  }

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate =
        typeChecked.length > 0 && typeChecked.length !== types.length
    }
  }, [typeChecked.length, types.length])

  const onClickNext = () => {
    if (selectedTypes.length !== 0) {
      setActiveStep(DOWNLOAD_STEP.DATE_AND_FORMAT)
    } else {
      setError('types', { shouldFocus: 'types' })
    }
  }

  const onClickBack = () => {
    setActiveStep(DOWNLOAD_STEP.PARAMETERS)
  }

  const onClickDownload = () => {
    const data = checkInputs()
    if (data === 'Invalid date') return
    return onSubmit(data)
  }

  const getDownloadMinDate = () => {
    if (isPremium) return undefined

    const date = new Date()
    date.setDate(date.getDate() - 30)

    return toDateInputValue(date)
  }

  return (
    <StylesProvider injectFirst>
      <div className={styles.modalContainer}>
        <Typography className={styles.header}>{translate('downloadData')}</Typography>
        <div className={styles.modal}>
          <form
            noValidate
            onSubmit={handleSubmit(onClickDownload)}
          >
            <div style={{ display: activeStep === DOWNLOAD_STEP.PARAMETERS ? 'block' : 'none' }}>
              <DownloadParametersStep
                errors={errors}
                onCancel={onClickCancel}
                onNext={onClickNext}
                onSelectAll={handleSelectAll}
                onTypeChange={handleChange}
                register={register}
                selectAllCheckboxRef={selectAllCheckboxRef}
                translate={translate}
                typeChecked={typeChecked}
                types={types}
              />
            </div>
            <div
              style={{
                display: activeStep === DOWNLOAD_STEP.DATE_AND_FORMAT ? 'block' : 'none'
              }}
            >
              <DownloadDateAndFormatStep
                control={control}
                errors={errors}
                fileExtensionValue={value}
                getDownloadMinDate={getDownloadMinDate}
                getFileExtensionLabel={getFileExtensionLabel}
                getGroupingLabel={getGroupingLabel}
                getOrderLabel={getOrderLabel}
                groupingValue={groupingValue}
                isPremium={isPremium}
                isSubmitting={isSubmitting}
                onBack={onClickBack}
                onCancel={onClickCancel}
                onChangeFileExtension={handleChangeFileExtension}
                onChangeGrouping={handleChangeGrouping}
                onChangeOrder={handleChangeOrder}
                orderValue={orderValue}
                submitError={submitError}
                system={system}
                translate={translate}
                validateDates={checkInputs}
              />
            </div>
          </form>
        </div>
      </div>
    </StylesProvider>
  )
}

DownloadSettingsForm.propTypes = {
  system: PropTypes.instanceOf(Map),
  translate: PropTypes.func.isRequired
}

export default withLocalize(DownloadSettingsForm)
